// Static demo payloads for no-Worker visual preview.
//
// Activated via `?fixture=1` (v3 set — Slice 8 wave 3 renderer) or
// `?fixture=2` (v4 set — Category → Item → Action redesign). The route
// dispatcher inspects `inFixtureMode()` and routes data calls through
// `fetchFixtureBriefingIndex` / `fetchFixtureBriefing` instead of the real
// API helpers, so the SPA can be loaded as plain static files (e.g. via
// `python3 -m http.server -d dist`) without a running Worker.
//
// Selection is by exact query-string match — the SPA only ever surfaces
// one fixture set at a time, so the briefing index isn't polluted with
// schema-mismatched entries.

import type {
  BriefingListItem, BriefingPayload, BriefingRecord, PhaseValue, ThreadView,
} from "./briefings";

// sessionStorage key for persisting the fixture choice across the
// SPA's pushState navigations. The router (`app.ts::navigate`) rewrites
// the URL to /runs/:runId on link clicks, which DROPS the original
// ?fixture=N query — so checking only `window.location.search` would
// flip back to v3 the moment the user clicks an index entry. Persist
// once at first detection and prefer the persisted value thereafter.
const FIXTURE_SET_KEY = "exec-assistant-portal-fixture-set";

// In-memory fallback for the fixture-set, persisted across the router's
// query-stripping pushState navigation WITHOUT relying on sessionStorage.
// sessionStorage can silently no-op (private window, blocked/isolated site data)
// — there `setItem` on first detection throws-and-is-ignored, so every later call
// (after the router drops ?fixture=N) read an EMPTY store and flipped fixture mode
// OFF: the SPA then loaded the v3 flat default on the next run-click and POSTed
// feedback to a (nonexistent) Worker instead of stubbing, so a reviewer saw "a flat
// project" + "Closed 0 of N" errors. A module variable survives pushState (no full
// reload), so fixture mode holds for the whole review session regardless of
// sessionStorage; a genuine full reload re-detects from the ?fixture=N entry URL.
let fixtureSetMemo: "v3" | "v4" | null = null;

export function inFixtureMode(): boolean {
  if (typeof window === "undefined") return false;
  const q = window.location.search;
  if (q.includes("fixture=1") || q.includes("fixture=2")) return true;
  if (fixtureSetMemo !== null) return true;
  try {
    return window.sessionStorage.getItem(FIXTURE_SET_KEY) !== null;
  } catch {
    return false;
  }
}

// Internal — returns the active fixture set ("v3" | "v4"). First call
// derives from `?fixture=N`; subsequent calls (after the SPA's router
// drops the query) read from sessionStorage. Defaults to "v3" so the
// legacy ?fixture=1 behavior is unchanged.
function activeFixtureSet(): "v3" | "v4" {
  if (typeof window === "undefined") return "v3";
  const q = window.location.search;
  let detected: "v3" | "v4" | null = null;
  if (q.includes("fixture=2")) detected = "v4";
  else if (q.includes("fixture=1")) detected = "v3";
  if (detected) {
    // In-memory first (always works, survives pushState), then sessionStorage as
    // a cross-reload hint (may silently no-op in a private/blocked context).
    fixtureSetMemo = detected;
    try {
      window.sessionStorage.setItem(FIXTURE_SET_KEY, detected);
    } catch {
      // Ignore — private-mode browsers may throw; the in-memory memo above keeps
      // fixture mode alive for the rest of this session's pushState navigation.
    }
    return detected;
  }
  if (fixtureSetMemo !== null) return fixtureSetMemo;
  try {
    const stored = window.sessionStorage.getItem(FIXTURE_SET_KEY);
    if (stored === "v3" || stored === "v4") return stored;
  } catch {
    // Fall through to default.
  }
  return "v3";
}

interface FixtureBriefing {
  run_id: string;
  week_start: string;
  week_end: string;
  payload: BriefingPayload;
}

const RECERT_THREAD = {
  slug: "electrical-recertification",
  name: "Electrical Recertification",
  status: "active",
  inferred: false,
  one_liner: "City filing remediation through May 15.",
};

const STUDIO_IDC_THREAD = {
  slug: "studio-idc-leveling",
  name: "Studio IDC Leveling",
  status: "active",
  inferred: false,
  one_liner: "RFP responses for the Q2 vendor short-list.",
};

const FIXTURES: FixtureBriefing[] = [
  // Slice 8 Wave 3 v3 fixture — exercises the new SPA renderer:
  // notched left-edge segments, Responsible Party display, compactness
  // toggle, Risk badge on impact-bearing non-stale action_items,
  // Pending bullets on compact items, and the candidate_threads tail
  // section with thread_promote / thread_reject controls.
  {
    run_id: "demo-2026-05-10",
    week_start: "2026-05-04",
    week_end: "2026-05-10",
    payload: {
      payload_schema_version: 3,
      project: { slug: "demo-project", name: "Demo Project" },
      week: { start: "2026-05-04", end: "2026-05-10" },
      executive_summary:
        "Pool permit blocker is the most consequential thread this week —" +
        " GC sign-off still outstanding and we lose the May 20 milestone if" +
        " it slips past Friday. Electrical recertification is on track.",
      threads: [
        {
          ...RECERT_THREAD,
          one_liner: "Final inspection scheduled; remaining work is closeout.",
          work_items: [
            {
              slug: "block-b-submission",
              name: "Block B submission",
              summary:
                "Filed and accepted — awaiting inspector's punch-list close.",
              status: "open",
              first_seen: "2026-04-12",
              responsible_party: {
                name: "Andrea Mosher",
                email: "andrea@example.com",
                source: "synthesized",
                locked_at: "2026-05-10",
              },
              compact: true,
              pending: [
                "Awaiting inspector punch-list close",
                "Closeout photos due 5/12",
              ],
              involved: ["Andrea Mosher", "Ray Lastra"],
              key_dates: ["2026-05-15: re-inspection window"],
              action_items: [
                {
                  what: "Submit closeout photos to inspector",
                  description: "Last item before the city can sign off.",
                  assignee: "Andrea Mosher",
                  due: "2026-05-12",
                  stale: false,
                },
              ],
            },
            {
              slug: "pool-permit",
              name: "Pool permit",
              summary:
                "GC sign-off outstanding; loses May 20 milestone if not resolved by Friday.",
              status: "open",
              first_seen: "2026-04-26",
              responsible_party: {
                name: "Sherry",
                source: "operator_override",
                locked_at: "2026-05-10",
              },
              compact: false,
              involved: ["Tom Iazzetta (GC)", "Sherry"],
              key_dates: ["2026-05-16: GC sign-off deadline", "2026-05-20: milestone"],
              action_items: [
                {
                  what: "Confirm pool permit re-issue with GC",
                  description: "Tom hasn't replied to the 5/4 escalation.",
                  assignee: "Sherry",
                  due: "2026-05-16",
                  impact: "Slips the May 20 milestone if not resolved this week",
                  recommended_next_step: {
                    text: "Escalate via Tom directly; copy the ownership group",
                    source_ref: "abcdef0123456789",
                    observed_at: "2026-05-04",
                    attribution: "Sherry → Tom email, 5/4",
                  },
                  stale: false,
                  first_seen: "2026-04-26",
                },
                {
                  what: "Old action carried from April",
                  assignee: "Andrea Mosher",
                  due: "2026-04-30",
                  stale: true,
                  first_seen: "2026-04-19",
                },
              ],
              leadership_attention: [
                {
                  what: "Pool permit re-issue stuck on GC",
                  why: "Direct quote from Tom 5/4: 'will confirm Monday' — Monday passed without a reply.",
                  recommend_action: "Recommend principal-level escalation if no reply by Thursday EOD",
                },
              ],
            },
          ],
          archived_work_items: [
            {
              slug: "phase-1-walkthrough",
              name: "Phase 1 walkthrough",
              closed_at: "2026-04-19",
              closed_reason: "completed",
            },
          ],
        },
      ],
      unthreaded: {
        work_items: [
          {
            slug: "march-hours",
            name: "March hours",
            summary: "Pending submission.",
            status: "open",
            responsible_party: {
              name: "Andrea Mosher",
              source: "default_pm",
              locked_at: "2026-05-10",
            },
            compact: true,
            pending: ["Submit March hours for Quayside project"],
          },
        ],
      },
      candidate_threads: [
        {
          slug: "wood-group-call",
          name: "Wood Group call",
          one_liner: "Three emails this week reference a 5/15 sync — promote or reject?",
          first_seen: "2026-05-08",
          bucket: "structural",
        },
        {
          slug: "interior-lab-pricing",
          name: "Interior Lab pricing",
          one_liner: "Ambiguous project assignment — could be Studio IDC or its own thread.",
          first_seen: "2026-05-09",
          bucket: "relevance_uncertain",
        },
      ],
      needs_review: [],
    },
  },
  {
    run_id: "demo-2026-05-03",
    week_start: "2026-04-27",
    week_end: "2026-05-03",
    payload: {
      project: { slug: "demo-project", name: "Demo Project" },
      week: { start: "2026-04-27", end: "2026-05-03" },
      executive_summary:
        "Closed the electrical recertification scope of work and locked the May" +
        " milestone schedule. One vendor blocker on Studio IDC is awaiting a" +
        " status response from the GC.",
      threads: [
        {
          ...RECERT_THREAD,
          completed: [
            { what: "Filed amended electrical permit with the city", owner: "Ray Lastra" },
            { what: "Inspector walkthrough completed; punch list is two items", owner: "Andrea Mosher" },
          ],
          in_progress: [
            { what: "Awaiting follow-up inspection date from city", owner: "Ray Lastra", status: "scheduled-this-week" },
          ],
          decisions: [
            {
              what:
                "Approved using EPC-rated rigid conduit on rooftop runs to meet" +
                " the city's amended NEC reference",
              decided_by: "Sherry",
              decided_at: "4/29/2026",
            },
          ],
          blockers: [
            {
              what: "GC has not confirmed availability for the re-inspection",
              owner: "Tom Iazzetta (GC)",
              impact: "Slips the May 15 milestone if not resolved this week",
              recommended_next_step: "Sherry to escalate via Tom directly; copy the ownership group",
            },
          ],
          action_items: [
            { what: "Submit final punch-list closeout photos to inspector", assignee: "Andrea Mosher", due: "5/8/2026" },
            { what: "Confirm rooftop conduit count with electrical sub", assignee: "Ray Lastra", due: "5/6/2026" },
          ],
          open_questions: [
            {
              question:
                "Is the amended filing sufficient for the May 15 milestone, or" +
                " do we need a separate temporary CO?",
              raised_by: "Andrea Mosher",
            },
          ],
          leadership_attention: [
            {
              what: "GC silence on the re-inspection slot is the biggest risk",
              why:
                "All other punch-list items are closeable this week; the only" +
                " external dependency is Tom's calendar.",
            },
          ],
        },
        {
          ...STUDIO_IDC_THREAD,
          completed: [
            {
              what: "Leveled Design/Vision RFP responses for Studio IDC, ROW01, and Interior Lab",
              owner: "Ray Lastra",
            },
          ],
          action_items: [
            { what: "Send revised pricing schedule to Studio IDC", assignee: "Ray Lastra", due: "5/4/2026" },
          ],
        },
      ],
      unthreaded: {
        action_items: [
          { what: "Submit March hours for Quayside project", assignee: "Andrea Mosher", due: "5/2/2026" },
        ],
        open_questions: [
          { question: "Does the new state filing window apply retroactively to Q1?", raised_by: "Sherry" },
        ],
      },
      needs_review: [
        {
          item_id: "abc123def456abcd",
          summary:
            "Email from carrier@vendor-x.com referencing 'electrical project'" +
            " — could match Recertification or a separate Studio IDC scope.",
          why_flagged: "External party with no project assignment in config.",
        },
      ],
    },
  },
  {
    run_id: "demo-2026-04-26",
    week_start: "2026-04-20",
    week_end: "2026-04-26",
    payload: {
      project: { slug: "demo-project", name: "Demo Project" },
      week: { start: "2026-04-20", end: "2026-04-26" },
      executive_summary:
        "Vendor scope finalized for the recertification work and an early-week" +
        " kickoff with the GC produced a clean schedule. Studio IDC RFP cycle" +
        " entered final revision.",
      threads: [
        {
          ...RECERT_THREAD,
          completed: [
            { what: "Vendor scope of work finalized and signed", owner: "Sherry" },
            { what: "Kickoff call held with the GC and electrical sub", owner: "Ray Lastra" },
          ],
          decisions: [
            {
              what: "Selected Atlas Electric over Northbeam after side-by-side cost review",
              decided_by: "Sherry",
              decided_at: "4/22/2026",
            },
          ],
          action_items: [
            { what: "Mail signed scope-of-work to vendor", assignee: "Andrea Mosher", due: "4/24/2026" },
          ],
        },
        {
          ...STUDIO_IDC_THREAD,
          in_progress: [
            { what: "Collecting final pricing from three vendors", owner: "Ray Lastra", status: "on-track" },
          ],
        },
      ],
      unthreaded: {},
      needs_review: [],
    },
  },
  {
    run_id: "demo-2026-04-19",
    week_start: "2026-04-13",
    week_end: "2026-04-19",
    payload: {
      project: { slug: "demo-project", name: "Demo Project" },
      week: { start: "2026-04-13", end: "2026-04-19" },
      executive_summary:
        "Quiet week — no decisions or action items beyond regular project" +
        " maintenance. RFP cycle started; two vendor introductions completed.",
      threads: [
        {
          ...STUDIO_IDC_THREAD,
          milestones: [
            {
              what: "RFP cycle opened with five candidate firms",
              significance: "Sets the Q2 vendor short-list cadence",
            },
          ],
          in_progress: [
            { what: "Vendor introductions and capability calls", owner: "Ray Lastra" },
          ],
          open_questions: [
            { question: "Should we limit the short-list to firms with prior NYC experience?", raised_by: "Sherry" },
          ],
        },
      ],
      unthreaded: {},
      needs_review: [
        {
          item_id: "ghost001ghost001",
          summary: "Calendar invite from unrecognized organizer 'Q2 Sync'.",
          why_flagged: "No matching project in config; could be admin spam.",
        },
      ],
    },
  },
];

// --- v4 fixture (Category → Item → Action redesign) -------------------------
//
// Activated via `?fixture=2`. Exercises the v4 payload shape:
// - Category (thread) with `display_number`
// - Item (work_item) with `narrative`, `display_number`, `kil_marked`
// - Action (action_item) with `priority_status`, `open_questions[]`,
//   `kil_marked`, `reason` / `impact` / `off_hold_condition`
// - Breadcrumb on a recently-moved Item (Decision 3.6)
// - Top-level `unfiled[]` panel (Decision 1.5)
// - Billing-aggregate Action with `invoices[]` (Section 11)
//
// Visual reference: `tmp/briefing-demo/index.html`. Renderer is Task #7;
// this fixture exists to validate the Pydantic + TS schema round-trip
// and pre-stage the v4 renderer's golden input.

const VARIANCE_APPROVAL_THREAD = {
  slug: "variance-approval",
  display_number: "1",
  name: "Variance Approval",
  status: "open" as const,
  inferred: false,
  one_liner: "Zoning variance for the rear setback.",
  first_seen: "2026-04-19",
  // #37: phase mix for the dogfood band layout — a populated early band.
  phase: "pre_development" as const,
};

const LOT_LINE_THREAD = {
  slug: "lot-line-confirmation",
  display_number: "2",
  name: "Lot Line Confirmation",
  status: "open" as const,
  inferred: false,
  one_liner: "Boundary survey + recorded easement walk-back.",
  first_seen: "2026-04-26",
  // #37: a second populated band, non-adjacent to pre_development so the empty
  // Pre-Construction band renders between them; General then hosts the NYI tail
  // with no general-phase Category (the degenerate band the amendment flagged).
  phase: "construction" as const,
};

const FIXTURES_V4: FixtureBriefing[] = [
  {
    run_id: "demo-v4-2026-05-22",
    week_start: "2026-05-16",
    week_end: "2026-05-22",
    payload: {
      payload_schema_version: 4,
      project: { slug: "demo-project-v4", name: "Demo Project (v4)", display_name: "Demo v4" },
      week: { start: "2026-05-16", end: "2026-05-22" },
      executive_summary:
        "Variance hearing is the live risk this week — zoning board has not" +
        " responded to the 5/15 follow-up. Lot line walk produced one new" +
        " open question for County Recorder.",
      unfiled: [
        {
          id: "u-a1b2c3",
          text: "New email from County Tax Assessor referencing parcel ID and an unscheduled visit",
          narrative:
            "Email arrived 5/20 from assessor's office; appears related to a" +
            " parcel adjacent to ours but mentions our address.",
          source_ref: "ingest-a1b2c3d4",
          // POST-resolution value, deliberately. `source_author` is resolved at
          // the publish boundary (`portal._ACTOR_FIELDS`), and an EXTERNAL
          // counterparty like a county assessor is never in the identity map,
          // so the ladder suppresses it and the SPA falls back to its
          // subject-only citation. A raw address here would show a render
          // production can no longer emit — the fixture is the operator's
          // documented verification surface, so it must not misrepresent it.
          source_author: null,
          source_timestamp: "2026-05-20T15:30:00Z",
          attribution: "County Tax Assessor email, 5/20",
          // #61: missing-info framing — exercises the "Likely an … — Missing:"
          // line in the unified queue.
          missing: ["which Item this belongs under", "responsible party"],
          suggested_tier: "action" as const,
          is_new: true,
        },
        {
          // S3 accept-assist: the agent resolved this hint to an existing Item →
          // exercises the one-click "✓ Merge into 1.1 …" affordance + Undo toast.
          // #510 nit-4: the hint names the target Item AS IT RENDERS ("Hearing
          // preparation", work_item variance-hearing-prep) so the real resolver's
          // token-boundary name match WOULD produce this stamp — the demo shows only
          // what the pipeline can actually emit.
          id: "u-merge1",
          text: "Follow-up on hearing exhibit list",
          narrative: "Relates to the Hearing preparation work already tracked.",
          source_ref: "ingest-merge1",
          hint: "Appears related to the Hearing preparation Item.",
          suggested_action: "merge_item" as const,
          suggested_target_work_item_slug: "variance-hearing-prep",
          suggested_target_thread_slug: "variance-approval",
          is_new: true,
        },
        {
          id: "u-d5e6f7",
          text: "Voicemail transcript: contractor mentions roof repair scope expansion",
          narrative: "Transcript references both Variance Approval AND a topic the synthesizer couldn't place.",
          source_ref: "ingest-d5e6f7a8",
          source_author: "Bill Lambert",
          source_timestamp: "2026-05-21T10:15:00Z",
          attribution: "Bill Lambert voicemail, 5/21",
          hint: "Appears related to closed Item I-12 (Roof Inspection)",
        },
        {
          // #1.4: a candidate thread FOLDED into the queue (the pipeline does
          // this on a live run; pre-baked here since fixtures skip the
          // pipeline). Type-neutral — no "Suggested Category" framing — and
          // candidate_slug retires the inferred thread when the client acts.
          id: "candidate-contractor-insurance-compliance",
          text: "Contractor Insurance & Compliance",
          narrative: "Recurring COI / compliance correspondence the agent grouped — you decide what it is.",
          source_ref: "candidate:contractor-insurance-compliance",
          candidate_slug: "contractor-insurance-compliance",
          missing: ["whether this is its own Category, Item, or Action — or part of something already tracked"],
          is_new: false,
        },
      ],
      // A² (2026-06-03): Needs-Your-Input Informational bucket demo —
      // FYI / no-tracked-work events routed here instead of fabricating
      // thin Items in the Category tree.
      informational: [
        {
          id: "info-9f8e7d",
          text: "5/22 weekly project call held — agenda distributed",
          narrative:
            "Routine weekly call; no decisions requiring tracked follow-up." +
            " Surfaced here rather than as a standalone Item.",
          source_ref: "ingest-9f8e7d6c",
          source_author: "Ray Lastra",
          source_timestamp: "2026-05-22T14:00:00Z",
          attribution: "Ray Lastra, 5/22",
          origin_thread_name: "Project Communications",
          origin_work_item_name: "Weekly Project Call",
          is_new: true,
        },
        {
          id: "info-3c2b1a",
          text: "Board ratified the parking-lot lighting decision",
          narrative:
            "Decision logged for the record; execution already tracked under Electrical.",
          source_ref: "ingest-3c2b1a09",
          source_author: "Champ Bennett",
          source_timestamp: "2026-05-19T09:00:00Z",
          attribution: "Champ Bennett, 5/19",
          origin_thread_name: "Electrical Recertification",
          is_new: false,
        },
      ],
      // #60 option (a): candidate_threads stays in the PAYLOAD (harness
      // telemetry) but is deliberately NOT rendered — the unified queue has
      // no "Suggested Categories" surface. This fixture entry pins that:
      // if it ever renders, the option-(a) contract regressed.
      candidate_threads: [
        {
          slug: "contractor-insurance-compliance",
          name: "Contractor Insurance & Compliance",
          one_liner: "Recurring COI / compliance correspondence — promote or place?",
          first_seen: "2026-05-18",
        },
      ],
      // A³/A² (2026-06-03): a dismissed-as-acknowledged entry → collapsed line.
      informational_acknowledged: [
        {
          id: "info-7a6b5c",
          text: "5/15 site safety memo circulated",
          narrative: "Acknowledged for the record; no tracked work.",
          source_ref: "ingest-7a6b5c4d",
          attribution: "Andrea Mosher, 5/15",
          acknowledged_at: "2026-05-23T16:00:00Z",
          acknowledged_by: "Ray Lastra",
          is_new: false,
        },
        {
          // CADENCE-REPORT-SUPPRESS: an agent-set-aside recurring weekly ritual.
          id: "info-ritual1",
          text: "Scale weekly update report distributed to BOD",
          narrative: "Recurring weekly report; no tracked work.",
          source_ref: "ingest-ritual1",
          acknowledged_at: "2026-05-23T04:15:00Z",
          auto_acknowledged: true,
          acknowledged_reason: "recurring weekly ritual",
          is_new: false,
        },
      ],
      threads: [
        {
          ...VARIANCE_APPROVAL_THREAD,
          work_items: [
            {
              slug: "variance-hearing-prep",
              serial: 1,
              display_number: "1.1",
              name: "Hearing preparation",
              narrative:
                "Compiling the response packet for the May 28 zoning board" +
                " hearing — site plan revisions, neighbor outreach log, and" +
                " variance justification memo are the three deliverables.",
              status: "open",
              is_new: true,
              start_date: "2026-04-19",
              kil_marked: true,
              // T1 report-parity (2026-10-28): the accepted 558 "Point" (now
              // projected onto the blob by stamp_point) — renders on the Key Issue
              // Log report page's Item header + echoed on each action's Point column.
              point: "Scale / KAMM",
              responsible_party: {
                name: "Sherry",
                source: "operator_override",
                locked_at: "2026-05-22",
              },
              action_items: [
                {
                  ai_slug: "draft-justification-memo",
                  serial: 1,
                  display_number: "1.1.1",
                  what: "Draft variance justification memo",
                  narrative:
                    "Cite the two precedents Sherry flagged from the May" +
                    " 6 call and attach the surveyor's affidavit.",
                  priority_status: "Critical",
                  status: "open",
                  reason: "Submission window closes 5/26",
                  impact: "Hearing slips to August cycle if we miss it",
                  due: "2026-05-24",
                  first_seen: "2026-05-10",
                  responsible_party: { name: "Sherry", source: "operator_override" },
                  // Slice 1 (action-keyed reports): placed in BOTH client reports —
                  // exercises the Summary + 558 pages AND the replace-whole un-place
                  // (removing it from one report must preserve the other). kil_marked
                  // (PSU) too, so this action shows in the reports lens's KIL + PSU groups.
                  report_tags: ["summary", "558"],
                  kil_marked: true,
                },
                {
                  // A1 demo: a merged Action — renders in "Recently Merged
                  // Actions" with a "Merged → X" pill (distinct from a close).
                  ai_slug: "duplicate-memo-note",
                  serial: 9,
                  display_number: "1.1.9",
                  what: "Duplicate memo note (folded in)",
                  narrative: "Folded into the justification memo Action.",
                  priority_status: "Active",
                  status: "closed",
                  closed_at: "2026-05-21",
                  closed_reason: "merged into 1.1.1 Draft variance justification memo",
                  kil_marked: false,
                  action_type: "regular",
                },
                {
                  ai_slug: "schedule-variance-hearing",
                  serial: 2,
                  display_number: "1.1.2",
                  what: "Schedule variance hearing with zoning board",
                  priority_status: "Delay Potential",
                  status: "open",
                  // A1 demo: a carryover Action judged MERGE this period ->
                  // the "Updated" pill on an Action (outlined, distinct from NEW).
                  is_updated: true,
                  reason: "Zoning board has not confirmed the May 28 slot",
                  impact: "Without confirmation we can't notify neighbors in time",
                  due: "2026-05-23",
                  first_seen: "2026-05-08",
                  open_questions_v4: [
                    {
                      // N2/#117 demo: an unanswered question the answer-proposer
                      // judged answerable in-source -> renders the proposed answer
                      // + one-click Accept (action_open_question_resolve).
                      qid: "demo-q-hearing",
                      text: "Is the May 28 slot still available, or has it shifted?",
                      raised_by: "Sherry",
                      raised_at: "2026-05-15",
                      resolution_candidate: {
                        answer: "Yes — the zoning board confirmed the May 28 "
                          + "9am slot on 5/20.",
                        source_ref: "ingest-zbc-confirm",
                        proposed_at: "2026-05-22",
                        source_subject: "RE: Variance hearing date",
                        source_author: "board clerk",
                        source_timestamp: "2026-05-20T09:00:00Z",
                      },
                    },
                  ],
                },
                {
                  ai_slug: "neighbor-outreach-log",
                  serial: 3,
                  display_number: "1.1.3",
                  what: "Finalize neighbor outreach log",
                  priority_status: "Active",
                  status: "open",
                  due: "2026-05-27",
                  first_seen: "2026-05-12",
                  responsible_party: { name: "Andrea Mosher", source: "default_pm" },
                  // #93 demo: a live close-suggestion so ?fixture=2 exercises
                  // the Close + "Keep open (dismiss proposal)" pairing.
                  closure_candidate: { reason: "Outreach log looks complete — no open items this week." },
                },
                {
                  ai_slug: "appeal-procedure-research",
                  serial: 4,
                  display_number: "1.1.4",
                  what: "Research appeal procedure if hearing denies the variance",
                  priority_status: "On Hold",
                  status: "open",
                  reason: "Premature until hearing outcome is known",
                  off_hold_condition: "Hearing concludes with a denial",
                  first_seen: "2026-05-08",
                  responsible_party: { name: "Sherry", source: "operator_override" },
                },
                {
                  ai_slug: "invoices-aggregate",
                  serial: 5,
                  display_number: "1.1.5",
                  what: "Invoices & Pay Applications",
                  action_type: "invoices_aggregate",
                  priority_status: "Active",
                  status: "open",
                  narrative: "Two invoices outstanding; one pay app under review.",
                  first_seen: "2026-04-30",
                  responsible_party: { name: "Andrea Mosher", source: "default_pm" },
                  invoices: [
                    {
                      number: "INV-2026-0418",
                      received_date: "2026-05-12",
                      amount: 4250.00,
                      status: "Under Review",
                      due_date: "2026-05-30",
                      source_ref: "ingest-inv-2026-0418",
                    },
                    {
                      number: "PAY-APP-3",
                      received_date: "2026-05-18",
                      amount: 18200.50,
                      status: "Received",
                      due_date: "2026-06-01",
                      source_ref: "ingest-pay-app-3",
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          ...LOT_LINE_THREAD,
          work_items: [
            {
              // B10 × Option Y: a done Item with 0 open Actions — closeable,
              // so its Move-to-Category form enables "Also close this Item".
              slug: "survey-closeout",
              serial: 9,
              display_number: "2.9",
              name: "Survey closeout",
              narrative: "All survey deliverables accepted; nothing outstanding.",
              status: "open",
              start_date: "2026-04-20",
              // #66 D1-D3 demo: a LABELS-ONLY overlap proposal (no
              // target_locators — the shape of every live blob until the
              // first post-promote cron). One-click still works because the
              // label is an EXACT, unique, untruncated Item name; a "…"
              // truncated or unmatched label would degrade to note-only.
              merge_candidate: {
                kind: "overlap" as const,
                text: "Possibly the same as: Western boundary survey",
                targets: ["Western boundary survey"],
                evidence: ["also tracked under"],
              },
              action_items: [],
            },
            {
              slug: "eastern-lot-line",
              serial: 2,
              display_number: "2.1",
              name: "Eastern lot line verification",
              narrative:
                "Surveyor walk on 5/19 confirmed the recorded line within 6\"" +
                " on the eastern side; western side still pending.",
              status: "open",
              // A1 (2026-07-22): a carryover Item (is_new absent) the narrative-
              // merger judged a MERGE this period -> renders the "Updated" pill
              // and the "new/updated" filter keeps it visible.
              is_updated: true,
              start_date: "2026-04-26",
              responsible_party: { name: "Ray Lastra", source: "synthesized" },
              // #66 PR3 demo: an `ambiguous` surface — the consult recognized
              // this may be a known matter but resolved >1 candidate, so it
              // nudges placement via the existing Merge affordance (renders the
              // ◇ accent note on the title; never a separate panel).
              matter_note: {
                kind: "ambiguous" as const,
                text: "Possibly part of: Lot Line & Easement Reconciliation",
                matter_ids: ["lot-line-easement-reconciliation"],
                targets: ["western-boundary-survey", "survey-closeout"],
              },
              // #66 D1-D3 demo: a LOCATOR-bearing overlap proposal coexisting
              // with the matter note above — the ✎ banner unions both target
              // sets (overlap first), shows the muted reason line, and Accept
              // one-clicks (1 open action → no review gate). The banner
              // dismiss key gains the `:overlap` suffix.
              merge_candidate: {
                kind: "overlap" as const,
                text: "Possibly the same as: Western boundary survey",
                targets: ["Western boundary survey"],
                target_locators: [
                  {
                    thread: "lot-line-confirmation",
                    item: "western-boundary-survey",
                    action: null,
                    label: "Western boundary survey",
                  },
                ],
                evidence: ["overlaps with"],
              },
              action_items: [
                {
                  ai_slug: "verify-eastern-lot",
                  serial: 6,
                  display_number: "2.1.1",
                  what: "Verify eastern lot line with County Recorder",
                  priority_status: "Delay Potential",
                  status: "open",
                  reason: "Recorder has not responded to the 5/12 records request",
                  impact: "Walk-back complete cannot be filed until records are reconciled",
                  due: "2026-05-26",
                  first_seen: "2026-05-12",
                  responsible_party: { name: "Ray Lastra", source: "synthesized" },
                  report_tags: ["summary"], // Slice 1 (action-keyed): Summary only
                  open_questions_v4: [
                    {
                      // The COMMON shape (61 of 64 live questions): a qid, no
                      // proposed answer. Demos the per-question ✎ on its own —
                      // the row Ray sees most often. Without a qid the pencil
                      // correctly does not render, which made this fixture
                      // unrepresentative: 95% of live questions carry one.
                      qid: "demo-q-easement",
                      text: "Does the 1987 recorded easement supersede the 2003 amendment?",
                      raised_by: "Ray Lastra",
                      raised_at: "2026-05-19",
                    },
                  ],
                },
              ],
            },
            {
              slug: "western-boundary-survey",
              serial: 3,
              display_number: "2.2",
              name: "Western boundary survey",
              narrative:
                "Recategorized from Variance Approval on 5/21 after the" +
                " surveyor's notes clarified the scope is purely about lot" +
                " lines, not setback.",
              status: "open",
              start_date: "2026-05-04",
              responsible_party: { name: "Ray Lastra", source: "synthesized" },
              breadcrumb: {
                former_path: "1.4",
                moved_at: "2026-05-21",
              },
              // #66 PR3 demo: an `ambiguous` surface note that DELIBERATELY
              // coexists with the move breadcrumb above (proves the two inline
              // annotations don't collide). Surface-only — the consult emits
              // only `ambiguous`; the target resolves to its item number.
              matter_note: {
                kind: "ambiguous" as const,
                text: "Possibly part of: Lot Line & Easement Reconciliation",
                matter_ids: ["lot-line-easement-reconciliation"],
                targets: ["survey-closeout"],
              },
              action_items: [
                {
                  ai_slug: "western-boundary-pin-set",
                  serial: 7,
                  display_number: "2.2.1",
                  what: "Set western boundary pins with surveyor",
                  priority_status: "On Hold",
                  status: "open",
                  reason: "Surveyor crew unavailable until 6/3",
                  off_hold_condition: "Surveyor crew back from booked engagement",
                  first_seen: "2026-05-04",
                  responsible_party: { name: "Ray Lastra", source: "synthesized" },
                  // Decision 3.6: action_move_to_item breadcrumb (this Action
                  // was re-parented from Item 1.4 — shows for 2 weeks).
                  breadcrumb: {
                    former_path: "1.4.2",
                    moved_at: "2026-05-21",
                  },
                  // #66 D1-D3 demo: an ACTION-level overlap note (inline ◇
                  // advisory only — no one-click at the Action tier in v1),
                  // deliberately coexisting with the breadcrumb above.
                  merge_candidate: {
                    kind: "overlap" as const,
                    text: "Possibly the same as: Verify eastern lot line with County Recorder",
                    targets: ["Verify eastern lot line with County Recorder"],
                    evidence: ["same work as"],
                  },
                },
              ],
            },
            {
              // #66 D4 demo: a NEW Item the dedup JUDGE flagged as a suspected duplicate
              // of 2.2 Western boundary survey (merge_candidate source: "judge"). It must
              // NOT render in this category body — isJudgeDup relocates it into
              // Needs-Your-Input's "Possible duplicates" group with its Merge banner.
              slug: "west-side-boundary-survey",
              serial: 99,
              display_number: "2.3",
              name: "West-side boundary survey",
              narrative:
                "Surveyor scheduling the western lot-line pins — likely the same work" +
                " already tracked under Western boundary survey.",
              status: "open",
              is_new: true,
              start_date: "2026-05-22",
              responsible_party: { name: "Ray Lastra", source: "synthesized" },
              merge_candidate: {
                kind: "overlap" as const,
                source: "judge" as const,
                confidence: "high" as const,
                text: "Possible duplicate of 2.2 Western boundary survey",
                targets: ["2.2 Western boundary survey"],
                target_locators: [
                  {
                    thread: "lot-line-confirmation",
                    item: "western-boundary-survey",
                    action: null,
                    label: "2.2 Western boundary survey",
                  },
                ],
                evidence: ["same western lot-line pin-setting work"],
              },
              action_items: [
                {
                  ai_slug: "west-side-pins",
                  serial: 100,
                  display_number: "2.3.1",
                  what: "Confirm western pin-set date with surveyor",
                  priority_status: "Active",
                  status: "open",
                  due: "2026-06-05",
                  first_seen: "2026-05-22",
                  responsible_party: { name: "Ray Lastra", source: "synthesized" },
                },
              ],
            },
          ],
        },
        // #60: a CLOSED Category so the File-under-Closed "Closed Category"
        // tier has a pickable target in ?fixture=2 (all three tiers must be
        // exercisable on the verification surface).
        {
          // A CLOSED Category — the File-under-Closed "Closed Category" tier's
          // pickable target. Dogfood feedback 2: it carries a within-window
          // recently-closed Item so it renders as a LEGIT recently-closed
          // Category (what #77 keeps for the 1-week window), not a stale empty
          // header (which #77 drops on a real run). The fixture is static and
          // never runs the pipeline, so #77 itself isn't exercised here.
          slug: "easement-recording",
          display_number: "3",
          name: "Easement Recording",
          status: "closed" as const,
          inferred: false,
          one_liner: "Utility easement recorded with the county — settled.",
          first_seen: "2026-04-01",
          closed_at: "2026-05-19",
          work_items: [
            {
              slug: "easement-doc-recording",
              serial: 30,
              display_number: "3.1",
              name: "Record the utility easement with the county",
              narrative: "County recorded the easement; nothing outstanding.",
              status: "closed" as const,
              closed_at: "2026-05-19",
              closed_reason: "recorded with the county",
              action_items: [],
            },
          ],
        },
        // #38: the canonical sub-category demo — THE ?fixture=2 verification
        // surface for this feature. "Electrical Recertification" carries three
        // groups (two open: Unit Work, Garage & Common; one closed tombstone:
        // Parking Lot), ungrouped sibling Items that render FIRST (legacy-prefix
        // rule), and a closed Item still pointing at the closed Parking Lot
        // group so the "Recently Closed Items" disclosure shows the "(was in
        // Parking Lot)" suffix. Numbers interleave across groups on purpose
        // (4.3/4.4 in Unit Work while ungrouped 4.1/4.2 sit above) — numbers are
        // identity, not position.
        {
          slug: "electrical-recertification",
          display_number: "4",
          name: "Electrical Recertification",
          status: "open" as const,
          inferred: false,
          // D4 (2026-10-16): a preset (template-seeded) Category — its pencil
          // gates rename/merge/move-to-phase/move-to-subproject (the demo keeps
          // Add Item / Add Sub-category / Comment). The other demo Categories
          // omit `preset` (recipient-added) and keep the full restructure menu.
          preset: true,
          first_seen: "2026-04-15",
          subcategories: [
            {
              slug: "unit-work", name: "Unit Work", status: "open" as const,
              created_at: "2026-05-02", seq: 1,
            },
            {
              slug: "garage-common", name: "Garage & Common",
              status: "open" as const, created_at: "2026-05-05", seq: 2,
            },
            {
              // Closed within the 14-day window → header-only tombstone row in
              // the group sequence (struck name + "Closed · DATE" + reopen).
              slug: "parking-lot", name: "Parking Lot", status: "closed" as const,
              created_at: "2026-05-06", closed_at: "2026-05-20",
              closed_reason: "Lot lighting energized and signed off", seq: 3,
            },
          ],
          work_items: [
            // Ungrouped siblings — render FIRST (the legacy-prefix rule).
            {
              slug: "main-service-upgrade", serial: 41, display_number: "4.1",
              name: "Main service upgrade", status: "open" as const,
              narrative:
                "400A service swap scheduled with the utility; meter release" +
                " pending.",
              action_items: [
                {
                  ai_slug: "confirm-meter-release", serial: 1,
                  display_number: "4.1.1",
                  what: "Confirm meter release date with the utility",
                  priority_status: "Active", status: "open" as const,
                  responsible_party: { name: "Ray Lastra", source: "synthesized" },
                  report_tags: ["558"], // Slice 1 (action-keyed): 558 only
                },
              ],
            },
            {
              slug: "permit-closeout", serial: 42, display_number: "4.2",
              name: "Permit closeout", status: "open" as const,
              narrative:
                "Final electrical permit open; awaiting the city's follow-up" +
                " inspection.",
              action_items: [],
            },
            // Grouped under Unit Work.
            {
              slug: "unit-101-rough-in", serial: 43, display_number: "4.3",
              name: "Unit 101 rough-in", status: "open" as const,
              subcategory_slug: "unit-work",
              narrative:
                "Rough-in inspection passed; trim-out scheduled next week.",
              action_items: [
                {
                  ai_slug: "schedule-trim-out", serial: 1,
                  display_number: "4.3.1", what: "Schedule unit 101 trim-out",
                  priority_status: "Active", status: "open" as const,
                  responsible_party: { name: "Champ Bennett", source: "synthesized" },
                },
              ],
            },
            {
              slug: "unit-102-panel", serial: 44, display_number: "4.4",
              name: "Unit 102 panel replacement", status: "open" as const,
              subcategory_slug: "unit-work",
              narrative:
                "Panel swap complete; awaiting AHJ sign-off on the load calc.",
              action_items: [],
            },
            // Grouped under Garage & Common.
            {
              slug: "garage-ev-charger", serial: 45, display_number: "4.5",
              name: "Garage EV charger circuits", status: "open" as const,
              subcategory_slug: "garage-common",
              narrative:
                "Two Level-2 circuits roughed; common-area sub-panel on order.",
              action_items: [
                {
                  ai_slug: "order-subpanel", serial: 1, display_number: "4.5.1",
                  what: "Order common-area sub-panel",
                  priority_status: "Delay Potential", status: "open" as const,
                  reason: "Long-lead item (8-week quote)",
                  impact: "Blocks the garage final inspection",
                  responsible_party: { name: "Ray Lastra", source: "synthesized" },
                },
              ],
            },
            // A CLOSED Item still pointing at the now-closed Parking Lot group →
            // renders in "Recently Closed Items" with "(was in Parking Lot)".
            {
              slug: "lot-light-poles", serial: 46, display_number: "4.6",
              name: "Parking-lot light poles", status: "closed" as const,
              subcategory_slug: "parking-lot",
              closed_at: "2026-05-20",
              narrative:
                "Poles set, energized, and inspected; closed with the Parking" +
                " Lot group.",
              action_items: [],
            },
          ],
        },
        // Hide-empty (Ray 2026-09-22) demo: an empty seed Category (no open
        // Items) — must be HIDDEN from BOTH the flat body AND the flat sidebar
        // TOC (no dangling anchor), and re-appear the moment it holds an Item.
        // The base flat run keeps ≥1 non-empty Category, so the project-wide
        // blank-guard is off and this one is genuinely hidden.
        {
          slug: "closeout-turnover",
          display_number: "5",
          name: "Closeout & Turnover",
          status: "open" as const,
          inferred: false,
          first_seen: "2026-04-15",
          work_items: [],
        },
      ],
      needs_review: [],
    },
  },
];

// P4c (R §P4): a SUB-PROJECTED variant of the v4 demo, derived from the flat
// entry above so the two stay in lock-step. It is the SECOND run in the set on
// purpose: `?fixture=2` still opens the flat run first (its DOM is the
// byte-identity oracle for a flat project), and the run switcher reaches this
// one. Exercises: `sub_projects[]` (A · North Tower, B · South Tower), the
// P4b `<letter><index>` numbers inherited down to Items/Actions, two empty
// standard Categories (the empty-category fold), and one recipient-added
// Category with a plain number + no sub_project (the P4b known limit → the
// trailing "Not in a sub-project" band). The `display_number`s here are the
// values the P4b allocator PRODUCES (letter from registry seq, index from the
// scale-standard template), not hand-invented shapes — a fixture must be able
// to produce the value it asserts.
function makeSubProjectFixture(base: FixtureBriefing): FixtureBriefing {
  const payload = JSON.parse(JSON.stringify(base.payload)) as BriefingPayload;
  const renumber = (node: { display_number?: string | null }, from: string, to: string): void => {
    const dn = node.display_number;
    if (!dn) return;
    if (dn === from) node.display_number = to;
    else if (dn.startsWith(`${from}.`)) node.display_number = `${to}${dn.slice(from.length)}`;
  };
  // Flat "1".."4" → A3 / A5 / B5 / B7 (template indexes 3 = Design / Drawings,
  // 5 = Permits, 7 = Construction Admin — the standard-set positions).
  const plan: Array<{ sp: string; num: string }> = [
    { sp: "north", num: "A3" }, { sp: "north", num: "A5" },
    { sp: "south", num: "B5" }, { sp: "south", num: "B7" },
  ];
  (payload.threads ?? []).forEach((t, i) => {
    const target = plan[i];
    if (!target) return;
    const from = t.display_number ?? "";
    t.sub_project_slug = target.sp;
    renumber(t, from, target.num);
    for (const wi of t.work_items ?? []) {
      renumber(wi, from, target.num);
      for (const ai of wi.action_items ?? []) renumber(ai, from, target.num);
    }
  });
  // P4d: place associations on a few rows so the Association lens / filter /
  // facet have data. Associations ORIGINATE on Actions and cascade up to the
  // Item (union); an Item can be multi-valued (Master + its own tower). B7 (th
  // index 3) is deliberately left with NO association → the "⚑ Needs
  // association" bucket + a demonstrable filter miss.
  // Association slugs are the REAL config values (estates: master / north-tower /
  // south-tower) so the restyle's per-slug colour chips (.atag[data-a=…]) render
  // in the demo exactly as they do in production — a fixture must produce the
  // values it asserts. (The sub_project slugs "north"/"south" are a SEPARATE
  // namespace — the P4b letter roots — and stay unchanged.)
  const th = payload.threads ?? [];
  const firstAction = (t?: ThreadView) => t?.work_items?.[0]?.action_items?.[0];
  const firstItem = (t?: ThreadView) => t?.work_items?.[0];
  // A2 (Ray 2026-09-15): associations live on ACTIONS; the Item chip is a pure
  // rollup (itemAssociationUnion). Tag ACTIONS so the demo renders exactly as
  // production does — an Item's chip derives from its Actions, not an own tag.
  const a0 = firstAction(th[0]); if (a0) a0.association_tags = ["north-tower"];         // Item[0] rolls up North Tower
  const a1 = firstAction(th[1]); if (a1) a1.association_tags = ["master", "north-tower"]; // Item rolls up Master + North
  const a3 = firstAction(th[3]); if (a3) a3.association_tags = ["south-tower"];           // Item rolls up South Tower
  // T1 report-parity demo: place a 558-tagged Item under a sub-category so the
  // Key Issue Log page shows the full Association → sub-project → sub-category →
  // Item nesting. th[3] (electrical) already defines a "Unit Work" sub-category,
  // and its first Item (main-service-upgrade) carries a 558 action.
  const b7Item = firstItem(th[3]); if (b7Item) b7Item.subcategory_slug = "unit-work";
  // An UNPLACED, OPEN Action carrying a proposer suggestion → the inline ghost `?`
  // chip (dashed .atag, click-to-confirm). Targeted by ai_slug (the schedule-
  // hearing Action) so the demo shows a placed chip (a0, on the same Item) and a
  // ghost chip at a glance — NOT the merged/closed duplicate that shares the Item.
  const ghostAct = (th[0]?.work_items?.[0]?.action_items ?? [])
    .find((a) => a.ai_slug === "schedule-variance-hearing");
  if (ghostAct) {
    ghostAct.association_tags = [];
    ghostAct.association_candidate = {
      associations: ["north-tower"],
      source_ref: "est-0514-a02",
      proposed_at: "2026-05-14",
      source_subject: "North Tower zoning hearing scheduling",
      source_author: "Sherry",
      source_timestamp: "2026-05-14",
    };
  }
  const emptyCat = (
    slug: string, num: string, name: string, phase: PhaseValue, sp = "north",
  ): ThreadView => ({
    slug, display_number: num, name, status: "open", inferred: false,
    one_liner: `${name} — nothing filed yet.`, first_seen: "2026-05-09",
    phase, sub_project_slug: sp, work_items: [],
  });
  payload.threads = [
    ...(payload.threads ?? []),
    emptyCat("north-concept-design", "A1", "Concept Design", "pre_development"),
    emptyCat("north-project-review", "A2", "Project Review / Due Diligence", "pre_development"),
    // Same standard Category in BOTH sub-projects → the Category lens groups
    // them under one "Concept Design" header (the real-data shape).
    emptyCat("south-concept-design", "B1", "Concept Design", "pre_development", "south"),
    {
      slug: "site-security", display_number: "30", name: "Site Security",
      status: "open", inferred: false,
      one_liner: "Recipient-added Category (no sub-project yet).",
      first_seen: "2026-05-12", phase: null, sub_project_slug: null,
      work_items: [{
        slug: "night-patrol", display_number: "30.1", name: "Night patrol contract",
        status: "open", narrative: "Two quotes in hand; board to pick one.",
        action_items: [{
          ai_slug: "pick-vendor", display_number: "30.1.1",
          text: "Select the night-patrol vendor", status: "open",
          priority_status: "Active", due: "2026-05-22",
        }],
      }],
    },
  ];
  payload.sub_projects = [
    // `master` is a non-exclusive association spanning BOTH towers (design:
    // Master is a peer; axis order Master / North / South). The sub-project
    // DISPLAY names are deliberately distinct from the association names
    // ("Building A/B" vs the North/South Tower associations) so the report pages
    // demo FOUR distinct tiers (Association → sub-project → sub-category → Item)
    // rather than reading as the association twice. (In the real axis projects a
    // sub-project and its association can be 1:1 — see the sub-project-tier
    // keep/drop review note in the premise-gate doc.)
    { slug: "north", name: "Building A", seq: 1, status: "open", associations: ["master", "north-tower"] },
    { slug: "south", name: "Building B", seq: 2, status: "open", associations: ["master", "south-tower"] },
  ];
  payload.executive_summary =
    "Sub-projected demo: North Tower carries the variance + lot-line work;" +
    " South Tower carries the easement close-out and recertification.";
  // R2: per-sub-project executive summaries — the outward/board headline for
  // each scope, rendered under its band header.
  payload.sub_project_summaries = {
    north: "North Tower's zoning variance and lot-line survey are the week's" +
      " critical path; the variance hearing is set and the survey is in review.",
    south: "South Tower's easement close-out is nearly complete and the" +
      " recertification package is with the reviewer awaiting comments.",
  };
  return {
    run_id: "demo-v4-sp-2026-05-15", week_start: "2026-05-09", week_end: "2026-05-15",
    payload,
  };
}
FIXTURES_V4.push(makeSubProjectFixture(FIXTURES_V4[0]!));

export async function fetchFixtureBriefingIndex(): Promise<BriefingListItem[]> {
  const set = activeFixtureSet() === "v4" ? FIXTURES_V4 : FIXTURES;
  return set.map((b) => ({
    run_id: b.run_id,
    week_start: b.week_start,
    week_end: b.week_end,
    stored_at: `${b.week_end}T18:00:00Z`,
  }));
}

export async function fetchFixtureBriefing(runId: string): Promise<BriefingRecord> {
  const set = activeFixtureSet() === "v4" ? FIXTURES_V4 : FIXTURES;
  const found = set.find((b) => b.run_id === runId) ?? set[0]!;
  return {
    project_slug: found.payload.project?.slug ?? "demo-project",
    run_id: found.run_id,
    week_start: found.week_start,
    week_end: found.week_end,
    stored_at: `${found.week_end}T18:00:00Z`,
    payload: found.payload,
  };
}

// v4 Task #10 — fixture data for the Closed Items tab. Mix of recently-
// closed items (within the 2-week briefing retention) and long-tail
// archive items (well outside), so the tab's value over the briefing's
// "Recently Closed" surface is obvious.
import type { ClosedItemsSnapshot } from "./closed_items";

const FIXTURE_CLOSED_ITEMS_V4: ClosedItemsSnapshot = {
  project_slug: "demo-project",
  snapshot_at: "2026-05-22T22:30:00Z",
  closed_items: [
    {
      slug: "block-b-submission",
      serial: 7,
      display_number: "1.2",
      // #90 step 10 — a renumbered row on a state-derived snapshot. Present so
      // `?fixture=2` demonstrates that the Closed Archive says the same thing
      // about a moved entity as the briefing does (ruling 2026-08-10).
      breadcrumb: { former_path: "1.4", kind: "recategorized", moved_at: "2026-05-21" },
      name: "Block B Submission",
      narrative: "Block B documents delivered to county on 2026-04-08. Permit issued 2026-04-12. Workstream complete.",
      summary: null,
      thread_slug: "permits",
      thread_name: "Permits & Approvals",
      thread_display_number: "1",
      first_seen: "2026-03-01",
      start_date: "2026-03-01",
      closed_at: "2026-04-12",
      closed_by: "Agent Operator",
      closed_reason: "permit issued",
      kil_marked: false,
      responsible_party: {
        name: "Andrea Mosher",
        email: "andrea@example.com",
        source: "operator_override",
        locked_at: "2026-03-01",
      },
      action_items: [
        {
          ai_slug: "submit-rfp-response",
          serial: 1,
          display_number: "1.2.1",
          text: "Submit RFP response to county",
          narrative: "Final markup attached. Awaiting county review.",
          priority_status: "Active",
          status: "closed",
          assignee: "Ray Lastra",
          due: "2026-04-08",
          first_seen: "2026-03-15",
          closed_at: "2026-04-08",
          closed_by: "Ray Lastra",
          closed_reason: "delivered",
          reason: null,
          impact: null,
          off_hold_condition: null,
          kil_marked: false,
          action_type: "regular",
        },
        {
          ai_slug: "track-permit-issuance",
          serial: 2,
          display_number: "1.2.2",
          text: "Track permit issuance",
          narrative: "Permit issued 2026-04-12.",
          priority_status: "Active",
          status: "closed",
          assignee: "Andrea Mosher",
          due: null,
          first_seen: "2026-04-08",
          closed_at: "2026-04-12",
          closed_by: "Agent Operator",
          closed_reason: "permit issued",
          reason: null,
          impact: null,
          off_hold_condition: null,
          kil_marked: false,
          action_type: "regular",
        },
      ],
    },
    {
      slug: "phase-1-walkthrough",
      serial: 3,
      display_number: "2.1",
      name: "Phase 1 Walkthrough",
      narrative: "Site walkthrough completed 2026-03-22. Punch list captured in separate Item.",
      summary: null,
      thread_slug: "site-evaluation",
      thread_name: "Site Evaluation",
      thread_display_number: "2",
      first_seen: "2026-02-15",
      start_date: "2026-02-15",
      closed_at: "2026-03-22",
      closed_by: "Agent Operator",
      closed_reason: "completed",
      kil_marked: true,
      responsible_party: {
        name: "Ray Lastra",
        email: "ray@example.com",
        source: "synthesized",
        locked_at: "2026-02-15",
      },
      action_items: [
        {
          ai_slug: "scheduling",
          serial: 1,
          display_number: "2.1.1",
          text: "Coordinate walkthrough schedule",
          narrative: "All parties confirmed 2026-03-22.",
          priority_status: "Active",
          status: "closed",
          assignee: "Ray Lastra",
          due: "2026-03-22",
          first_seen: "2026-02-20",
          closed_at: "2026-03-22",
          closed_by: "Ray Lastra",
          closed_reason: "completed",
          reason: null,
          impact: null,
          off_hold_condition: null,
          kil_marked: false,
          action_type: "regular",
        },
      ],
    },
    {
      slug: "abandoned-vendor-search",
      serial: 11,
      display_number: "3.4",
      name: "Vendor Y Evaluation",
      narrative: "Vendor Y disqualified on pricing. Workstream closed without selection.",
      summary: null,
      thread_slug: "procurement",
      thread_name: "Procurement",
      thread_display_number: "3",
      first_seen: "2025-12-01",
      start_date: "2025-12-01",
      closed_at: "2026-01-15",
      closed_by: "Agent Operator",
      closed_reason: "vendor disqualified",
      kil_marked: false,
      responsible_party: null,
      action_items: [],
    },
  ],
  // B2 preview data — the "Acknowledged / Not Tracked" Archive section.
  acknowledged_items: [
    {
      slug: "swaysland-pool-warranty",
      serial: 42,
      display_number: "3.4",
      name: "Swaysland pool warranty follow-up",
      narrative: null,
      summary: null,
      thread_slug: "site-eval",
      thread_name: "Site Evaluation",
      thread_display_number: "3",
      first_seen: "2026-05-01",
      acknowledged_at: "2026-06-23T14:00:00Z",
      acknowledged_by: "Ray Lastra",
      kil_marked: false,
    },
  ],
  acknowledged_actions: [
    {
      ai_slug: "fyi-vendor-newsletter",
      serial: 7,
      display_number: "1.2.3",
      text: "Reviewed vendor monthly newsletter (FYI only)",
      work_item_slug: "vendor-coordination",
      work_item_name: "Vendor Coordination",
      thread_slug: "general",
      acknowledged_at: "2026-06-23T14:05:00Z",
      acknowledged_by: "Ray Lastra",
    },
  ],
  // #82 — open Items carrying standalone closed Actions, rendered as Item cards
  // (same construct) tagged "Open · contains closed actions".
  open_items_with_closed_actions: [
    {
      slug: "firm-selection",
      serial: 41,
      display_number: "1.1",
      status: "open",
      name: "Firm Selection",
      narrative:
        "Design-firm shortlist in progress; committee reviewing the third " +
        "round of RFP responses.",
      summary: null,
      thread_slug: "visioning-rfp",
      thread_name: "Visioning RFP",
      thread_display_number: "1",
      first_seen: "2026-06-07",
      start_date: "2026-06-07",
      closed_at: null,
      closed_by: null,
      closed_reason: null,
      kil_marked: false,
      responsible_party: {
        name: "Ray Lastra", email: null, source: "synthesized", locked_at: null,
      },
      action_items: [
        {
          ai_slug: "follow-up-with-firms-after-committee",
          serial: 12,
          display_number: "1.1.4",
          text: "Follow up with firms after committee meeting",
          narrative: null,
          priority_status: "Active",
          status: "closed",
          assignee: null,
          due: null,
          first_seen: "2026-06-07",
          closed_at: "2026-06-22",
          closed_by: null,
          closed_reason: null,
          reason: null,
          impact: null,
          off_hold_condition: null,
          kil_marked: false,
          action_type: "regular",
        },
        {
          ai_slug: "release-common-area-rfp",
          serial: 13,
          display_number: "1.1.7",
          text: "Release Common Area Design RFP to the new design teams",
          narrative: null,
          priority_status: "Active",
          status: "closed",
          assignee: null,
          due: null,
          first_seen: "2026-06-07",
          closed_at: "2026-06-22",
          closed_by: "Marcus Chaidez",
          closed_reason: "merged into firm-selection-rfp",
          reason: null,
          impact: null,
          off_hold_condition: null,
          kil_marked: false,
          action_type: "regular",
        },
      ],
    },
  ],
};

export async function fetchFixtureClosedItems(): Promise<ClosedItemsSnapshot> {
  return FIXTURE_CLOSED_ITEMS_V4;
}

// #40 — fixture data for the Hours Log tab. Demonstrates §A (a busy week + a
// quieter week) with Scale-team attribution (confirmed plain / "possibly"),
// a NEW badge, and §B (an open item gone quiet across the window).
import type { HoursEntriesMe, HoursLogSnapshot } from "./hours_log";

const FIXTURE_HOURS_LOG_V4: HoursLogSnapshot = {
  project_slug: "demo-project",
  snapshot_at: "2026-06-21T22:30:00Z",
  counts: { weeks: 4, active_items: 4, no_activity_items: 2 },
  grouped_available: true,   // G2: exercise the ungrouped/grouped toggle in ?fixture=2
  run_id: "demo-run",        // G2 Phase B: for inline tagging (no-op POST in fixture mode)
  // G2 (Phase A): entity order (config order) + label for the grouped read-out.
  // The items below carry association_tags that exercise every bucket in the
  // most-recent billing month (2026-06): single-entity, multi-under-each, and
  // untagged→Unallocated — so the grouped view can't conceal those paths.
  entity_label: "Association",
  associations: [
    { slug: "master", name: "Master" },
    { slug: "north-tower", name: "North Tower" },
    { slug: "south-tower", name: "South Tower" },
  ],
  weeks: [
    {
      week_start: "2026-06-15",
      week_end: "2026-06-21",
      items: [
        {
          slug: "parking-closeout",
          display_number: "1.1",
          name: "Parking Lot Punch List & Closeout",
          phase: "construction",
          thread_name: "Site Work",
          thread_display_number: "1",
          association_tags: ["north-tower"],   // single entity
          sub_project: "site-work",
          narrative:
            "Punch list walked 6/18; three lighting items remain before the " +
            "contractor's final pay application can be released.",
          is_new: false,
          actions_summary:
            "Confirm pole bases energized; Release contractor final pay " +
            "application; Close out punch list",
          attribution: { confirmed: ["Ray Lastra"], possibly: ["Robert Farcas"] },
          actions: [
            {
              ai_slug: "a1", display_number: "1.1.1",
              text: "Confirm pole bases energized",
              narrative: "Awaiting the inspector's sign-off, expected this week.",
              priority_status: "Active",
            },
          ],
        },
        {
          slug: "recert-scope",
          display_number: "2.3",
          name: "Recertification Scope Decision",
          phase: "pre_construction",
          thread_name: "40-Year Recertification",
          thread_display_number: "2",
          association_tags: ["master", "north-tower"],   // multi → under EACH
          sub_project: "recertification",
          thread_slug: "forty-year-recertification",     // for the inline-tag target_id
          narrative:
            "Board asked for a phased-scope option; Andrea is assembling the " +
            "comparison for the next meeting.",
          is_new: true,
          actions_summary: "Assemble phased-scope comparison for the board",
          attribution: { confirmed: [], possibly: ["Andrea Mosher"] },
          // G2 Phase B: Actions tagged per association drive the per-entity split
          // narratives; the untagged one feeds the Unallocated sub-row.
          actions: [
            {
              ai_slug: "r1", display_number: "2.3.1",
              text: "Assemble phased-scope comparison (North Tower portion)",
              narrative: null, priority_status: "Active",
              association_tags: ["north-tower"],
            },
            {
              ai_slug: "r2", display_number: "2.3.2",
              text: "Reconcile Master-scope change orders for the board packet",
              narrative: null, priority_status: "Active",
              association_tags: ["master"],
            },
            {
              ai_slug: "r3", display_number: "2.3.3",
              text: "Draft the combined board cover memo",
              narrative: null, priority_status: "Active",
              association_tags: [],
            },
          ],
        },
      ],
    },
    {
      week_start: "2026-06-08",
      week_end: "2026-06-14",
      items: [
        {
          slug: "m2e-reconciliation",
          display_number: "2.5",
          name: "M2E Final Payment Reconciliation",
          phase: "pre_construction",
          thread_name: "40-Year Recertification",
          thread_display_number: "2",
          association_tags: ["south-tower"],   // single entity (another)
          sub_project: "recertification",
          narrative: "Final invoice reconciled against the approved change orders.",
          is_new: false,
          actions_summary: "Reconcile final invoice against approved change orders",
          attribution: { confirmed: ["Ray Lastra"], possibly: [] },
          actions: [],
        },
      ],
    },
    {
      week_start: "2026-06-01",
      week_end: "2026-06-07",
      items: [
        {
          slug: "board-review",
          display_number: "3.1",
          name: "Board Review Meeting",
          phase: "general",
          thread_name: "Governance",
          thread_display_number: "3",
          association_tags: [],   // untagged → Unallocated
          sub_project: null,
          narrative: "Monthly board review — recertification status and budget.",
          is_new: false,
          actions_summary: "Prepare recertification status and budget summary for the board",
          attribution: { confirmed: ["Andrea Mosher"], possibly: ["Ray Lastra"] },
          actions: [],
        },
      ],
    },
    { week_start: "2026-05-25", week_end: "2026-05-31", items: [] },
  ],
  no_activity: [
    {
      slug: "warranty-tracking",
      display_number: "1.4",
      name: "Pool Plaster Warranty Tracking",
      phase: "construction",
      thread_name: "Site Work",
      thread_display_number: "1",
      narrative:
        "Warranty registered with the manufacturer; no follow-up needed until " +
        "the one-year inspection.",
      last_touched: "2026-05-10",   // #40c — real last-touched week
      last_active: "2026-05-12",
      attribution: { confirmed: [], possibly: ["Robert Farcas"] },
    },
    {
      slug: "reserve-study-update",
      display_number: "2.1",
      name: "Reserve Study Update",
      thread_name: "Finance",
      thread_display_number: "2",
      narrative: "Dormant — awaiting the board's FY27 budget cycle.",
      last_touched: null,           // #40c — dormant beyond the lookback
      last_active: "2026-06-28",
      attribution: { confirmed: [], possibly: [] },
    },
  ],
};

export async function fetchFixtureHoursLog(): Promise<HoursLogSnapshot> {
  return FIXTURE_HOURS_LOG_V4;
}

// §7 Phase-2: the dogfood/fixture recipient IS on the roster, so ?fixture=2
// exercises the entry cells + Save (the Save PUT is a no-op in fixture mode —
// app.ts guards writes). One week is pre-seeded so a reviewer sees filled
// cells + a non-zero personal total on load.
export async function fetchFixtureHoursEntriesMe(): Promise<HoursEntriesMe> {
  return {
    can_enter: true,
    display_name: "Ray Lastra",
    // HL-6b: the demo recipient is an HR admin, so ?fixture=2 exercises the
    // teammate-editable grid + the ✎ marker (Save is a no-op in fixture mode).
    can_edit_all: true,
    weeks: [
      {
        week_end: "2026-06-21",
        rows: [
          { row_key: "item:parking-closeout", label: "Parking Lot Punch List & Closeout", hours: 6 },
          // G2 Phase B: a per-entity split entry (recert-scope is multi-association).
          { row_key: "item:recert-scope:north-tower", label: "Recertification Scope Decision — North Tower", hours: 1 },
        ],
        misc: { office_admin: 2, non_billable: 0, pto: 0 },
        updated_at: "2026-06-22T09:00:00Z",
      },
    ],
  };
}

// HL-6a: the all-staff grid fixture — the demo recipient (Ray) is a member on an
// opted-in project, so ?fixture=2 renders the multi-column grid: Ray's column is
// editable (from /me), Andrea's + Robert's are read-only. Shared items (both Ray
// and Robert logged parking-closeout / recert-scope) exercise the column layout.
export async function fetchFixtureHoursEntriesAll(): Promise<HoursEntriesAll> {
  return {
    can_view_all: true,
    can_edit_all: true,
    members: [
      {
        id: "m-ray", name: "Ray Lastra", is_self: true,
        weeks: [{
          week_end: "2026-06-21",
          rows: [
            { row_key: "item:parking-closeout", label: "Parking Lot Punch List & Closeout", hours: 6 },
            { row_key: "item:recert-scope:north-tower", label: "Recertification Scope Decision — North Tower", hours: 1 },
          ],
          misc: { office_admin: 2, non_billable: 0, pto: 0 },
          updated_at: "2026-06-22T09:00:00Z",
        }],
      },
      {
        id: "m-andrea", name: "Andrea Mosher", is_self: false,
        weeks: [
          {
            week_end: "2026-06-21",
            // HL-6c: an HR-entered + LOCKED cell (Ray filled it for Andrea) — the
            // 🔒/🔓 chip renders per-cell. G2 Phase B: recert-scope splits per
            // entity (Master + North Tower); the Master leg carries the lock.
            rows: [
              {
                row_key: "item:recert-scope:master", label: "Recertification Scope Decision — Master",
                hours: 2, locked: true, edited_by_name: "Ray Lastra",
              },
              { row_key: "item:recert-scope:north-tower", label: "Recertification Scope Decision — North Tower", hours: 1 },
            ],
            misc: { office_admin: 0, non_billable: 0, pto: 8 },
            updated_at: "2026-06-22T10:00:00Z",
          },
          {
            week_end: "2026-06-14",
            rows: [{ row_key: "item:m2e-reconciliation", label: "M2E Final Payment Reconciliation", hours: 4.5 }],
            misc: null, updated_at: "2026-06-15T10:00:00Z",
          },
        ],
      },
      {
        id: "m-robert", name: "Robert Farcas", is_self: false,
        weeks: [
          {
            week_end: "2026-06-21",
            rows: [
              { row_key: "item:parking-closeout", label: "Parking Lot Punch List & Closeout", hours: 2 },
              { row_key: "item:recert-scope:north-tower", label: "Recertification Scope Decision — North Tower", hours: 1.5 },
              // G2 Phase B: a LEGACY (pre-split) base entry — shows in the None
              // row, reassignable; proves legacy hours aren't zeroed by the derive.
              { row_key: "item:recert-scope", label: "Recertification Scope Decision", hours: 0.5 },
              // A custom "Also worked" row (non-Item). It bills under Unallocated in
              // the workbook, so the Grouped read-out surfaces it as an Unallocated
              // "Also worked" line — otherwise the on-screen Σ under-reports the bill.
              { row_key: "custom:0621-s0", label: "Weekly all-hands meeting", hours: 1.5 },
            ],
            misc: null, updated_at: "2026-06-22T11:00:00Z",
          },
          {
            week_end: "2026-06-07",
            rows: [{ row_key: "item:board-review", label: "Board Review Meeting", hours: 2 }],
            misc: null, updated_at: "2026-06-08T09:00:00Z",
          },
        ],
      },
    ],
  };
}

import type { HoursEntriesAll, HoursLogGenerationsResponse } from "./hours_log";

export async function fetchFixtureHoursLogGenerations(): Promise<HoursLogGenerationsResponse> {
  return {
    generations: [
      {
        id: "demo-hl-1",
        generated_at: "2026-06-22T14:05:00Z",
        generated_by: "ray@scalecol.com",
        filename: "Quayside-Hours-Log-2026-06-21.xlsx",
        label: null,
      },
    ],
  };
}

// P1r slice 5 — fixture data for the report pages' generations archive. Only
// the Summary has a live backend renderer (P5a); the 558 renderer is deferred
// (P5a-558), so its Generate is gated in the SPA and it has NO generations yet.
// The fixture reflects that: a Summary archive entry, none for the 558.
import type { ReportGenerationsResponse } from "./report_marks";

export async function fetchFixtureReportGenerations(): Promise<ReportGenerationsResponse> {
  return {
    project_slug: "demo-project-v4",
    generations: [
      {
        id: "demo-rep-summary-1",
        report_id: "summary",
        generated_at: "2026-05-22T15:10:00Z",
        generated_by: "ray@scalecol.com",
        week_end: "2026-05-24",
        filename: "2026.0524 Quayside-Summary.docx",
        label: null,
        files: [
          { report: "summary", axis: null, format: "docx", filename: "summary.docx" },
        ],
      },
    ],
  };
}

// v4 Task #11 — fixture data for the Key Issues Log tab.
import type { KilMarksSnapshot } from "./kil_marks";

const FIXTURE_KIL_MARKS_V4: KilMarksSnapshot = {
  project_slug: "demo-project",
  snapshot_at: "2026-05-22T22:30:00Z",
  counts: { marked_items: 3, marked_actions: 3, context_items: 0 },
  // SPA report-parity (T1, 2026-10-28): the ordered association bands + the
  // project executive summary the agent now carries on the web snapshot, so the
  // PSU page's Exec-Summary → Association → sub-category grouping is exercised in
  // the demo (a fixture must produce the values it asserts). Items carry an
  // `association` union; western-lot-line is multi-valued (Master + North Tower)
  // so it renders under EACH band (O1).
  associations: [
    { slug: "master", name: "Master" },
    { slug: "north-tower", name: "North Tower" },
    { slug: "south-tower", name: "South Tower" },
  ],
  executive_summary:
    "North Tower's variance hearing is the week's critical path; the western "
    + "lot-line survey and the county permit renewal are the two open items behind it.",
  items: [
    {
      slug: "hearing-prep",
      serial: 1,
      display_number: "1.1",
      // Same crumb, one tier up on the PSU/KIL view — the second surface the
      // 2026-08-10 ruling covers.
      breadcrumb: { former_path: "2.3", kind: "renumbered", moved_at: "2026-05-21" },
      name: "Hearing preparation",
      narrative: "Compiling the response packet for the May 28 zoning board hearing — site plan revisions, neighbor outreach log, and variance justification memo are the three deliverables.",
      summary: null,
      status: "open",
      association: ["north-tower"],
      thread_slug: "variance",
      thread_name: "Variance Approval",
      thread_display_number: "1",
      phase: "pre_development",
      first_seen: "2026-04-19",
      start_date: "2026-04-19",
      closed_at: null,
      responsible_party: {
        name: "Sherry",
        email: null,
        source: "synthesized",
        locked_at: "2026-04-19",
      },
      action_items: [
        {
          ai_slug: "submit-variance-memo",
          serial: 1,
          display_number: "1.1.1",
          text: "Submit variance justification memo to zoning board",
          narrative: "Drafted; awaiting Sherry's review before submission.",
          priority_status: "Critical",
          status: "open",
          assignee: "Sherry",
          due: "2026-05-26",
          first_seen: "2026-04-22",
          closed_at: null,
          reason: "Variance hearing scheduled May 28; missing memo blocks submission window.",
          impact: "Missed hearing would push project schedule 8 weeks.",
          off_hold_condition: null,
          kil_marked: true,
          action_type: "regular",
        },
      ],
    },
    {
      slug: "western-lot-line",
      serial: 4,
      display_number: "2.2",
      name: "Western boundary survey",
      narrative: "Surveyor walk on 5/19 confirmed the recorded line within 6\" on the eastern side; western side still pending.",
      summary: null,
      status: "open",
      association: ["master", "north-tower"],
      thread_slug: "lot-line",
      thread_name: "Lot Line Confirmation",
      thread_display_number: "2",
      phase: "construction",
      first_seen: "2026-05-04",
      start_date: "2026-05-04",
      closed_at: null,
      responsible_party: {
        name: "Ray Lastra",
        email: null,
        source: "synthesized",
        locked_at: "2026-05-04",
      },
      action_items: [
        {
          ai_slug: "schedule-western-walk",
          serial: 2,
          display_number: "2.2.1",
          text: "Schedule surveyor return for western boundary",
          narrative: "Surveyor proposed 5/27 or 5/30. Waiting on Ray's confirmation.",
          priority_status: "Delay Potential",
          status: "open",
          assignee: "Ray Lastra",
          due: "2026-05-27",
          first_seen: "2026-05-19",
          closed_at: null,
          reason: "Hearing packet requires both lot-line boundaries confirmed.",
          impact: "Missing the western survey blocks the variance memo's accuracy attestation.",
          off_hold_condition: null,
          kil_marked: true,
          action_type: "regular",
        },
      ],
    },
    {
      // #4: a marked Action whose Item sits inside a sub-category — the
      // PSU must surface the full Category › Sub-category › Item path.
      // Shares thread "variance" with hearing-prep (ungrouped) so the
      // fixture exercises BOTH the ungrouped and grouped partitions.
      slug: "permit-renewal",
      serial: 7,
      display_number: "1.3",
      name: "Building permit renewal",
      narrative: "Annual building permit lapses 6/30; the renewal application and county fee are the two outstanding pieces.",
      summary: null,
      status: "open",
      association: ["south-tower"],
      thread_slug: "variance",
      thread_name: "Variance Approval",
      thread_display_number: "1",
      phase: "pre_development",
      subcategory_slug: "permits-filing",
      subcategory_name: "Permits & Filing",
      first_seen: "2026-05-10",
      start_date: "2026-05-10",
      closed_at: null,
      responsible_party: {
        name: "Sherry",
        email: null,
        source: "synthesized",
        locked_at: "2026-05-10",
      },
      action_items: [
        {
          ai_slug: "file-permit-renewal",
          serial: 3,
          display_number: "1.3.1",
          text: "File building permit renewal with the county",
          narrative: "Application drafted; fee check pending board signature.",
          priority_status: "Active",
          status: "open",
          assignee: "Sherry",
          due: "2026-06-25",
          first_seen: "2026-05-10",
          closed_at: null,
          reason: null,
          impact: null,
          off_hold_condition: null,
          kil_marked: true,
          action_type: "regular",
        },
      ],
    },
  ],
  // Key Issue Log (558) actions closed inside the PSU's 1-week window — the
  // Key Issue Log report page merges these (the briefing payload strips closed
  // actions). 4.1.2 groups under the existing 4.1 card; 4.3.1 is a new card.
  key_issue_log: {
    closed_actions: [
      {
        thread_slug: "electrical-recertification", thread_name: "Electrical Recertification",
        thread_display_number: "4",
        item_slug: "main-service-upgrade", item_name: "Main service upgrade",
        item_display_number: "4.1",
        action: {
          ai_slug: "submit-load-calcs", display_number: "4.1.2",
          text: "Submit load calculations to the utility", status: "closed",
          closed_at: "2026-05-20T14:00:00Z", report_tags: ["558"],
          narrative: "Load calculations submitted and accepted by the utility.",
        } as import("./briefings").AnyItem,
      },
      {
        thread_slug: "electrical-recertification", thread_name: "Electrical Recertification",
        thread_display_number: "4",
        item_slug: "panel-labeling", item_name: "Panel labeling",
        item_display_number: "4.3",
        action: {
          ai_slug: "relabel-panels", display_number: "4.3.1",
          text: "Relabel the 3rd-floor panels", status: "closed",
          closed_at: "2026-05-19T10:00:00Z", report_tags: ["558"],
        } as import("./briefings").AnyItem,
      },
    ],
  },
};

export async function fetchFixtureKilMarks(): Promise<KilMarksSnapshot> {
  return FIXTURE_KIL_MARKS_V4;
}

// v4 Task #12 — fixture archive of past KIL .docx generations.
import type { KilGenerationsResponse } from "./kil_marks";

const FIXTURE_KIL_GENERATIONS_V4: KilGenerationsResponse = {
  project_slug: "demo-project",
  generations: [
    {
      id: "gen-20260520-001",
      generated_at: "2026-05-20T15:32:00Z",
      generated_by: "operator@example.com",
      week_end: "2026-05-17",
      filename: "2026.0517 DemoProject-Key-Issues.xlsx",
      label: "Prepped for 5/21 board meeting",
      marked_items: 1,
      marked_actions: 2,
      has_xlsx: true,  // #111b — a new Excel-only generation (single Excel icon)
    },
    {
      id: "gen-20260513-001",
      generated_at: "2026-05-13T11:04:00Z",
      generated_by: "operator@example.com",
      week_end: "2026-05-10",
      filename: "2026.0510 DemoProject-Key-Issues.docx",
      label: null,
      marked_items: 2,
      marked_actions: 3,
      has_docx: true,  // #111b — a historical Word-only generation (single Word icon)
    },
  ],
};

export async function fetchFixtureKilGenerations(): Promise<KilGenerationsResponse> {
  return FIXTURE_KIL_GENERATIONS_V4;
}
